import { US_NF_NUTRIENTS } from "./nutrients";
import type { CalcInput } from "./calculate";
import type { NutrientId } from "./types";

export interface ValidationResult {
  /** Block generation — the panel is not legal/complete. */
  errors: string[];
  /** Advisory — the panel may generate, but a human should review. */
  warnings: string[];
}

/**
 * Mandatory nutrients that may be *legitimately omitted* when below the insignificant-amount
 * threshold (21 CFR 101.9(c) / (f) footnote 1). When absent, the panel prints
 * "Not a significant source of ___" rather than erroring.
 */
const SUPPRESSIBLE: ReadonlySet<NutrientId> = new Set([
  "fat_sat",
  "fat_trans",
  "cholesterol",
  "fiber",
  "sugars_total",
  "sugars_added",
]);

const MANDATORY_IDS: NutrientId[] = US_NF_NUTRIENTS.filter((n) => n.mandatory).map(
  (n) => n.id,
);

const nameOf = (id: NutrientId): string => {
  const def = US_NF_NUTRIENTS.find((n) => n.id === id);
  return def?.names["en-US"] ?? id;
};

/**
 * Validate a US Nutrition Facts input against the §8 required-field rules.
 *
 * ERRORs (block generation):
 * - a non-suppressible mandatory nutrient is absent;
 * - the serving lacks a household measure or a metric weight;
 * - servings per container is missing.
 *
 * WARNINGs (advisory):
 * - polyunsaturated declared without monounsaturated (or vice versa) — §1.1 footnote 2.
 *
 * Out of M1 scope (later tiers): order/micro-order checks (the engine assembles in fixed
 * order so they can't be violated here), suppressed-without-footnote (buildPanel emits the
 * footnote), and Supplement Facts / EU / CA rules.
 */
export function validate(input: CalcInput): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (input.market !== "US") {
    errors.push(`market ${input.market} validation not implemented in M1`);
    return { errors, warnings };
  }

  // Serving.
  const serving = input.serving;
  if (!serving || !serving.householdMeasure || serving.householdMeasure.trim() === "") {
    errors.push("Serving size is missing its household measure (e.g. \"2/3 cup\").");
  }
  if (!serving || !(Number.isFinite(serving.metricGrams) && serving.metricGrams > 0)) {
    errors.push("Serving size is missing its metric weight in grams.");
  }
  if (
    !serving ||
    serving.servingsPerContainer == null ||
    (typeof serving.servingsPerContainer === "number" &&
      !(Number.isFinite(serving.servingsPerContainer) && serving.servingsPerContainer > 0))
  ) {
    errors.push("Servings per container is required.");
  }

  // Nutrient amounts must not be negative (21 CFR 101.9(c) amounts are non-negative). A negative
  // amount would otherwise flow through to a negative %DV (e.g. "−6%"). Zero is legal — Trans Fat
  // and Cholesterol are declared as 0 on the FDA sample — so this guards `< 0`, not `<= 0`.
  // Non-finite amounts (Infinity / NaN) pass a bare `< 0` check yet produce a garbage %DV, so
  // reject them explicitly. (The Supplement Facts validator applies the same finite guard.)
  for (const n of input.nutrients) {
    if (typeof n.amount === "number") {
      if (!Number.isFinite(n.amount)) {
        errors.push(`${nameOf(n.id)}: amount must be a finite number.`);
      } else if (n.amount < 0) {
        errors.push(`${nameOf(n.id)}: amount must not be negative.`);
      }
    }
  }

  // Declared nutrient ids (kept as raw strings so we can also see voluntary ids like
  // fat_poly/fat_mono that aren't in the mandatory NutrientId model).
  const declared = new Set<string>(input.nutrients.map((n) => String(n.id)));

  // Required-field gate: non-suppressible mandatory nutrients must be present.
  for (const id of MANDATORY_IDS) {
    if (SUPPRESSIBLE.has(id)) continue;
    if (!declared.has(id)) {
      errors.push(`${nameOf(id)} is required and is missing.`);
    }
  }

  // Fatty-acid symmetry (§1.1 footnote 2): poly and mono must both appear if either does.
  const hasPoly = declared.has("fat_poly");
  const hasMono = declared.has("fat_mono");
  if (hasPoly !== hasMono) {
    warnings.push(
      "Polyunsaturated and monounsaturated fat must both be declared when either one is.",
    );
  }

  return { errors, warnings };
}
